import { Component, ViewChild, inject } from '@angular/core';
import { GridApi, GridReadyEvent, ProcessDataFromClipboardParams } from 'ag-grid-community';
import { firstValueFrom, take } from 'rxjs';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { GridTransaction } from 'src/app/shared/components/grid/grid-transaction';
import { EfateCakeDataSet, EfateCakeExperimentData } from 'src/app/shared/models/echo/efate-cake-data-set';
import { CakeTopologyBidirectionalDirection, EfCakeTopologyGroupDefinition, EfCakeTopologyUiState } from 'src/app/shared/models/ef-cake-topology.model';
import { EfDataExperimentRow, EfDataPercentColumn, EfDataSaveTransaction, EfDataSetRow } from 'src/app/shared/models/ef-data.model';
import { EfateSetupApiService } from 'src/app/shared/services/echo/efate-setup.api.service';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { EfCakeTopologySharedService } from '../../shared/services/ef-cake-topology-shared.service';
import { EfCakeTopologyStateUtils } from '../../shared/utils/ef-cake-topology-state.utils';
import { EFDataColDef } from './ef-data-coldef';

@Component({
  selector: 'app-ef-data',
  templateUrl: './ef-data.component.html',
  styleUrls: ['./ef-data.component.css']
})
export class EFDataComponent {
  private readonly efateSetupApiService = inject(EfateSetupApiService);
  private readonly efCakeTopologySharedService = inject(EfCakeTopologySharedService);
  private readonly efDataColDef = inject(EFDataColDef);

  @ViewChild('grid') grid?: GridComponent;

  columnDefs: any[] = [];
  rowData: EfDataSetRow[] = [];
  showDetail: boolean = false;
  expandedRow?: EfDataSetRow;

  dataGroupOptions: EfCakeTopologyGroupDefinition[] = [];
  topologyNodeNames: Record<string, string> = { ...EFateConstants.DEFAULT_NODE_NAMES };
  selectedGroupColor: string = EFateConstants.GROUP_COLORS[0];
  selectedGroupActiveEdges = new Set<string>();
  selectedGroupActiveStatusNodes = new Set<string>();
  selectedGroupBidirectionalEdges: Record<string, CakeTopologyBidirectionalDirection> = { ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS };

  percentColumns: EfDataPercentColumn[] = [];
  experimentColumnDefs: any[] = [];
  experimentRowData: EfDataExperimentRow[] = [];
  experimentDefaultColDef = {
    resizable: true,
    suppressMovable: true,
  };

  private efateSetupPk?: number;
  private topologyState?: EfCakeTopologyUiState;
  private defaultGroupKey: string = EFateConstants.DEFAULT_GROUP_KEY;
  private dataSets: EfDataSetRow[] = [];
  private deletedDataSets: EfDataSetRow[] = [];
  private experimentGridApi?: GridApi;
  private pendingSave: Promise<boolean> = Promise.resolve(true);

  constructor() {
    this.columnDefs = this.efDataColDef.getDataSetColumnsDefinition(false, this);
  }

  async loadReadOnlyCakeTopology(efateSetupPk?: number): Promise<void> {
    this.efateSetupPk = efateSetupPk;
    this.topologyState = await this.efCakeTopologySharedService.getCakeTopologyStateBySetupPk(efateSetupPk);
    this.topologyNodeNames = { ...EFateConstants.DEFAULT_NODE_NAMES, ...(this.topologyState?.nodeNames ?? {}) };
    this.dataGroupOptions = this.buildDataGroupOptions();
    this.defaultGroupKey = this.resolveDefaultGroupKey();
    await this.loadDataSets();
  }

  async saveDataSets(efateSetupPk?: number): Promise<boolean> {
    const save = this.pendingSave
      .catch(() => false)
      .then(() => this.persistDataSets(efateSetupPk));

    this.pendingSave = save;

    return save;
  }

  private async persistDataSets(efateSetupPk?: number): Promise<boolean> {
    if (!this.efateSetupPk || this.efateSetupPk <= 0) {
      return true;
    }

    if (efateSetupPk && efateSetupPk !== this.efateSetupPk) {
      return false;
    }

    this.syncExpandedExperimentData();
    const transactions = this.buildSaveTransactions();

    if (transactions.length === 0) {
      return true;
    }

    try {
      await firstValueFrom(this.efateSetupApiService.saveEfateCakeDataSet(transactions).pipe(take(1)));
      if (this.grid) {
        this.grid.transactionList = [];
      }
      this.deletedDataSets = [];
      await this.reconcileSavedDataSets();
      return true;
    } catch (error) {
      console.warn('Error saving CAKE data sets', error);
      return false;
    }
  }

  onAdd(newItems: any): void {
    const newRow: EfDataSetRow = Array.isArray(newItems) ? newItems[0] : newItems;
    if (!newRow) {
      return;
    }

    newRow.efateSetupPk = this.efateSetupPk ?? 0;
    newRow.studyType = newRow.studyType ?? '';
    newRow.dataSetName = newRow.dataSetName ?? '';
    newRow.dataGroupKey = newRow.dataGroupKey || this.defaultGroupKey;
    newRow.experimentData = newRow.experimentData ?? [];

    if (!this.dataSets.includes(newRow)) {
      this.dataSets.unshift(newRow);
    }
  }

  onDelete(transactions: GridTransaction[]): void {
    const transactionList = Array.isArray(transactions) ? transactions : [];

    transactionList.forEach((transaction: GridTransaction) => {
      const row: EfDataSetRow = typeof transaction?.getRow === 'function' ? transaction.getRow() : (transaction as any)?.row;
      if (!row) {
        return;
      }

      const index = this.dataSets.indexOf(row);
      if (index > -1) {
        this.dataSets.splice(index, 1);
      }

      if ((row.efateCakeDataSetPk ?? 0) > 0 && !this.deletedDataSets.includes(row)) {
        this.deletedDataSets.push(row);
      }
    });
  }

  onDataSetFieldChanged(): void {
    this.grid?.gridApi?.refreshCells({ force: true, columns: [EFDataColDef.EXPAND_COLUMN_ID] });
  }

  async onButtonExpandClick(params: any): Promise<void> {
    const row: EfDataSetRow = params?.data;
    if (!row) {
      return;
    }

    if (row.isExpanded) {
      this.expandDataSet(row);
      return;
    }

    await this.collapseExpandedRow();
  }

  async onDataGroupChange(groupKey: string): Promise<void> {
    if (!this.expandedRow || !groupKey || groupKey === this.expandedRow.dataGroupKey) {
      return;
    }

    this.expandedRow.dataGroupKey = groupKey;
    this.expandedRow.modified = true;
    this.applySelectedGroup(groupKey);
    this.experimentRowData = this.getGroupExperimentRows(this.expandedRow, groupKey);
    this.clearHiddenPercentValues();

    await this.saveDataSets();
  }

  onExperimentGridReady({ api }: GridReadyEvent): void {
    this.experimentGridApi = api;
  }

  onExperimentCellValueChanged(): void {
    if (this.expandedRow) {
      this.expandedRow.modified = true;
    }

    this.ensureTrailingBlankRow();
  }

  processDataFromClipboard = (params: ProcessDataFromClipboardParams): string[][] | null => {
    const data = (params.data ?? []).map((row: string[]) => [...row]);

    while (data.length > 0 && data[data.length - 1].every((cell: string) => cell === null || cell === undefined || `${cell}`.trim() === '')) {
      data.pop();
    }

    if (data.length === 0) {
      return null;
    }

    const startRowIndex = params.api.getFocusedCell()?.rowIndex ?? 0;
    const missingRows = startRowIndex + data.length - params.api.getDisplayedRowCount();

    for (let rowIndex = 0; rowIndex < missingRows; rowIndex++) {
      this.appendBlankExperimentRow();
    }

    return data;
  };

  showDialog(_element: any, action: string, params?: any, _nodeId?: any): void {
    if (action !== 'delete' || !params?.data) {
      return;
    }

    if (params.isDataSetGrid) {
      params.data.deleted = true;
      this.grid?.deleteRowFromGrid(params);
      return;
    }

    this.removeExperimentRow(params.data as EfDataExperimentRow);
  }

  private async loadDataSets(): Promise<void> {
    this.resetExpandedState();
    this.dataSets = [];
    this.deletedDataSets = [];
    this.rowData = this.dataSets;
    this.refreshColumnDefs();

    if (!this.efateSetupPk || this.efateSetupPk <= 0) {
      return;
    }

    try {
      const dataSets = await firstValueFrom(this.efateSetupApiService.getEfateCakeDataSetsBySetupPk(this.efateSetupPk).pipe(take(1)));
      this.dataSets = (dataSets ?? []).map((dataSet: EfateCakeDataSet) => this.mapDataSetToRow(dataSet));
    } catch (error) {
      console.warn('Error loading CAKE data sets', error);
      this.dataSets = [];
    }

    this.rowData = this.dataSets;
  }

  private mapDataSetToRow(dataSet: EfateCakeDataSet): EfDataSetRow {
    return {
      efateCakeDataSetPk: dataSet.efateCakeDataSetPk,
      efateSetupPk: dataSet.efateSetupPk,
      studyType: dataSet.studyType ?? '',
      dataSetName: dataSet.dataSetName ?? '',
      dataGroupKey: dataSet.dataGroupKey || this.defaultGroupKey,
      experimentData: (dataSet.experimentData ?? []).map((experimentData: EfateCakeExperimentData, index: number) => ({
        ...experimentData,
        dataGroupKey: experimentData.dataGroupKey || dataSet.dataGroupKey || this.defaultGroupKey,
        sortOrder: experimentData.sortOrder ?? index,
      })),
      deletable: true,
      sortable: true,
    };
  }

  private buildSaveTransactions(): EfDataSaveTransaction[] {
    const transactions: EfDataSaveTransaction[] = [];

    this.dataSets.forEach((row: EfDataSetRow) => {
      if (!this.isSavableDataSet(row)) {
        return;
      }

      const isNewDataSet = (row.efateCakeDataSetPk ?? 0) <= 0;
      transactions.push({
        transactionType: isNewDataSet ? GridTransaction.insert : GridTransaction.update,
        row: this.mapRowToDataSet(row),
      });
    });

    this.deletedDataSets.forEach((row: EfDataSetRow) => {
      transactions.push({
        transactionType: GridTransaction.delete,
        row: this.mapRowToDataSet(row),
      });
    });

    return transactions;
  }

  private isSavableDataSet(row: EfDataSetRow): boolean {
    return (row.dataSetName ?? '').trim() !== '' || (row.studyType ?? '').trim() !== '';
  }

  private mapRowToDataSet(row: EfDataSetRow): EfateCakeDataSet {
    const dataSetPk = row.efateCakeDataSetPk ?? 0;

    return {
      efateCakeDataSetPk: dataSetPk,
      efateSetupPk: this.efateSetupPk ?? row.efateSetupPk,
      studyType: row.studyType ?? '',
      dataSetName: row.dataSetName ?? '',
      dataGroupKey: row.dataGroupKey || this.defaultGroupKey,
      experimentData: (row.experimentData ?? [])
        .filter((experimentRow: EfDataExperimentRow) => !this.isBlankExperimentRow(experimentRow))
        .map((experimentRow: EfDataExperimentRow) => ({
          efateCakeExperimentDataPk: experimentRow.efateCakeExperimentDataPk ?? 0,
          efateCakeDataSetPk: dataSetPk,
          dataGroupKey: experimentRow.dataGroupKey || row.dataGroupKey || this.defaultGroupKey,
          sortOrder: experimentRow.sortOrder ?? 0,
          timeDays: experimentRow.timeDays ?? null,
          parentPercent: experimentRow.parentPercent ?? null,
          a1Percent: experimentRow.a1Percent ?? null,
          a2Percent: experimentRow.a2Percent ?? null,
          a3Percent: experimentRow.a3Percent ?? null,
          b1Percent: experimentRow.b1Percent ?? null,
          b2Percent: experimentRow.b2Percent ?? null,
          c1Percent: experimentRow.c1Percent ?? null,
        })),
    };
  }

  private expandDataSet(row: EfDataSetRow): void {
    this.expandedRow = row;
    this.showDetail = true;
    this.rowData = [row];
    this.experimentGridApi = undefined;
    row.experimentDataByGroup = this.buildExperimentDataByGroup(row);
    row.dataGroupKey = this.defaultGroupKey;
    this.applySelectedGroup(row.dataGroupKey);
    this.experimentRowData = this.getGroupExperimentRows(row, row.dataGroupKey);
    this.clearHiddenPercentValues();
    this.refreshColumnDefs();
  }

  private async collapseExpandedRow(): Promise<void> {
    this.syncExpandedExperimentData();
    this.resetExpandedState();
    this.rowData = this.dataSets;
    this.refreshColumnDefs();
    await this.saveDataSets();
  }

  private async reconcileSavedDataSets(): Promise<void> {
    if (!this.efateSetupPk || this.efateSetupPk <= 0) {
      return;
    }

    let savedDataSets: EfateCakeDataSet[] = [];

    try {
      savedDataSets = await firstValueFrom(this.efateSetupApiService.getEfateCakeDataSetsBySetupPk(this.efateSetupPk).pipe(take(1))) ?? [];
    } catch (error) {
      console.warn('Error reading back CAKE data sets', error);
      return;
    }

    const unmatched = [...savedDataSets].sort((first, second) => (first.efateCakeDataSetPk ?? 0) - (second.efateCakeDataSetPk ?? 0));

    this.dataSets
      .filter((row: EfDataSetRow) => (row.efateCakeDataSetPk ?? 0) > 0)
      .forEach((row: EfDataSetRow) => this.matchSavedDataSet(row, unmatched,
        (savedDataSet: EfateCakeDataSet) => savedDataSet.efateCakeDataSetPk === row.efateCakeDataSetPk));

    this.dataSets
      .filter((row: EfDataSetRow) => (row.efateCakeDataSetPk ?? 0) <= 0 && this.isSavableDataSet(row))
      .forEach((row: EfDataSetRow) => this.matchSavedDataSet(row, unmatched,
        (savedDataSet: EfateCakeDataSet) => (savedDataSet.dataSetName ?? '').trim().toLowerCase() === (row.dataSetName ?? '').trim().toLowerCase()));
  }

  private matchSavedDataSet(row: EfDataSetRow, unmatched: EfateCakeDataSet[], predicate: (savedDataSet: EfateCakeDataSet) => boolean): void {
    const index = unmatched.findIndex(predicate);
    if (index < 0) {
      return;
    }

    this.applySavedKeys(row, unmatched.splice(index, 1)[0]);
  }

  private applySavedKeys(row: EfDataSetRow, savedDataSet: EfateCakeDataSet): void {
    row.efateCakeDataSetPk = savedDataSet.efateCakeDataSetPk;
    row.newRow = false;
    row.modified = false;

    const savedRowsByGroupAndOrder = new Map<string, EfateCakeExperimentData>();
    (savedDataSet.experimentData ?? []).forEach((savedRow: EfateCakeExperimentData) => {
      savedRowsByGroupAndOrder.set(`${savedRow.dataGroupKey ?? ''}|${savedRow.sortOrder ?? 0}`, savedRow);
    });

    (row.experimentData ?? []).forEach((experimentRow: EfDataExperimentRow) => {
      const savedRow = savedRowsByGroupAndOrder.get(`${experimentRow.dataGroupKey ?? ''}|${experimentRow.sortOrder ?? 0}`);
      if (!savedRow) {
        return;
      }

      experimentRow.efateCakeExperimentDataPk = savedRow.efateCakeExperimentDataPk;
      experimentRow.efateCakeDataSetPk = savedRow.efateCakeDataSetPk;
    });
  }

  private resetExpandedState(): void {
    if (this.expandedRow) {
      this.expandedRow.isExpanded = false;
    }

    this.expandedRow = undefined;
    this.showDetail = false;
    this.experimentGridApi = undefined;
    this.experimentRowData = [];
    this.experimentColumnDefs = [];
    this.percentColumns = [];
  }

  private syncExpandedExperimentData(): void {
    if (!this.expandedRow) {
      return;
    }

    const experimentDataByGroup = this.expandedRow.experimentDataByGroup ?? {};
    const experimentRows: EfDataExperimentRow[] = [];

    Object.keys(experimentDataByGroup).forEach((groupKey: string) => {
      experimentDataByGroup[groupKey]
        .filter((experimentRow: EfDataExperimentRow) => !this.isBlankExperimentRow(experimentRow))
        .forEach((experimentRow: EfDataExperimentRow, index: number) => {
          experimentRow.dataGroupKey = groupKey;
          experimentRow.sortOrder = index;
          experimentRows.push(experimentRow);
        });
    });

    this.expandedRow.experimentData = experimentRows;
  }

  private refreshColumnDefs(): void {
    this.columnDefs = this.efDataColDef.getDataSetColumnsDefinition(this.showDetail, this);
  }

  private applySelectedGroup(groupKey: string): void {
    const snapshot = this.topologyState?.topologyByGroup?.[groupKey];

    this.selectedGroupActiveEdges = new Set<string>(snapshot?.activeEdges ?? []);
    this.selectedGroupActiveStatusNodes = new Set<string>(snapshot?.activeStatusNodes ?? EFateConstants.DEFAULT_ACTIVE_STATUS_NODES);
    this.selectedGroupBidirectionalEdges = {
      ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS,
      ...(snapshot?.bidirectionalEdges ?? {}),
    };
    this.selectedGroupColor = this.getGroupColor(groupKey);
    this.percentColumns = this.buildPercentColumns();
    this.experimentColumnDefs = this.efDataColDef.getExperimentColumnsDefinition(this.percentColumns, this);
  }

  private buildPercentColumns(): EfDataPercentColumn[] {
    return EFateConstants.TOPOLOGY_NODE_KEYS
      .filter((nodeKey: string) => !!EFateConstants.NODE_KEY_TO_PERCENT_FIELD[nodeKey]
        && EfCakeTopologyStateUtils.isNodeActivated(nodeKey, this.selectedGroupActiveEdges, this.selectedGroupBidirectionalEdges))
      .map((nodeKey: string) => ({
        nodeKey,
        field: EFateConstants.NODE_KEY_TO_PERCENT_FIELD[nodeKey],
        headerName: `${this.topologyNodeNames[nodeKey] || nodeKey} (%)`,
      }));
  }

  private clearHiddenPercentValues(): void {
    const visibleFields = this.percentColumns.map((percentColumn: EfDataPercentColumn) => percentColumn.field);
    const allFields = Object.values(EFateConstants.NODE_KEY_TO_PERCENT_FIELD);

    this.experimentRowData.forEach((experimentRow: EfDataExperimentRow) => {
      allFields
        .filter((field: string) => !visibleFields.includes(field))
        .forEach((field: string) => {
          experimentRow[field] = null;
        });
    });
  }

  private buildExperimentDataByGroup(row: EfDataSetRow): Record<string, EfDataExperimentRow[]> {
    const experimentDataByGroup: Record<string, EfDataExperimentRow[]> = {};
    this.dataGroupOptions.forEach((group: EfCakeTopologyGroupDefinition) => {
      experimentDataByGroup[group.groupKey] = [];
    });

    (row.experimentData ?? []).forEach((experimentRow: EfDataExperimentRow) => {
      const groupKey = experimentRow.dataGroupKey || row.dataGroupKey || this.defaultGroupKey;
      experimentDataByGroup[groupKey] = experimentDataByGroup[groupKey] ?? [];
      experimentDataByGroup[groupKey].push(experimentRow);
    });

    Object.keys(experimentDataByGroup).forEach((groupKey: string) => {
      experimentDataByGroup[groupKey].sort((first, second) => (first.sortOrder ?? 0) - (second.sortOrder ?? 0));
    });

    return experimentDataByGroup;
  }

  private getGroupExperimentRows(row: EfDataSetRow, groupKey: string): EfDataExperimentRow[] {
    row.experimentDataByGroup = row.experimentDataByGroup ?? {};
    row.experimentDataByGroup[groupKey] = row.experimentDataByGroup[groupKey] ?? [];

    const experimentRows = row.experimentDataByGroup[groupKey];
    const lastRow = experimentRows[experimentRows.length - 1];

    if (!lastRow || !this.isBlankExperimentRow(lastRow)) {
      experimentRows.push(this.createBlankExperimentRow(groupKey));
    }

    return experimentRows;
  }

  private ensureTrailingBlankRow(): void {
    const lastRow = this.experimentRowData[this.experimentRowData.length - 1];
    if (lastRow && this.isBlankExperimentRow(lastRow)) {
      return;
    }

    this.appendBlankExperimentRow();
  }

  private appendBlankExperimentRow(): void {
    const blankRow = this.createBlankExperimentRow(this.expandedRow?.dataGroupKey || this.defaultGroupKey);
    this.experimentRowData.push(blankRow);
    this.experimentGridApi?.applyTransaction({ add: [blankRow] });
  }

  private removeExperimentRow(row: EfDataExperimentRow): void {
    const index = this.experimentRowData.indexOf(row);
    if (index < 0) {
      return;
    }

    this.experimentRowData.splice(index, 1);
    this.experimentGridApi?.applyTransaction({ remove: [row] });
    this.ensureTrailingBlankRow();

    if (this.expandedRow) {
      this.expandedRow.modified = true;
    }
  }

  private createBlankExperimentRow(groupKey: string): EfDataExperimentRow {
    const blankRow: EfDataExperimentRow = { dataGroupKey: groupKey, sortOrder: 0, timeDays: null };

    Object.values(EFateConstants.NODE_KEY_TO_PERCENT_FIELD).forEach((field: string) => {
      blankRow[field] = null;
    });

    return blankRow;
  }

  private isBlankExperimentRow(row: EfDataExperimentRow): boolean {
    if (!row) {
      return true;
    }

    const fields = ['timeDays', ...Object.values(EFateConstants.NODE_KEY_TO_PERCENT_FIELD)];
    return fields.every((field: string) => row[field] === null || row[field] === undefined || `${row[field]}`.trim() === '');
  }

  private buildDataGroupOptions(): EfCakeTopologyGroupDefinition[] {
    const groups = this.topologyState?.groups ?? [];

    if (groups.length === 0) {
      return [{
        groupKey: EFateConstants.DEFAULT_GROUP_KEY,
        groupName: `${EFateConstants.DEFAULT_GROUP_NAME_PREFIX} 1`,
        topologyKey: EFateConstants.DEFAULT_TOPOLOGY_KEY,
      }];
    }

    return groups;
  }

  private resolveDefaultGroupKey(): string {
    return this.dataGroupOptions[0]?.groupKey ?? EFateConstants.DEFAULT_GROUP_KEY;
  }

  private getGroupColor(groupKey: string): string {
    const groupIndex = this.dataGroupOptions.findIndex((group: EfCakeTopologyGroupDefinition) => group.groupKey === groupKey);
    return EFateConstants.GROUP_COLORS[groupIndex >= 0 ? groupIndex : 0] ?? EFateConstants.GROUP_COLORS[0];
  }
}
